import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { fetchStudentKpis, fetchStudentsPage } from '@services/registry';

export function useStudents({ tenantId, page = 1, pageSize = 10, includeRows = true }) {
    const cursorByPageRef = useRef(new Map([[1, null]]));

    useEffect(() => {
        cursorByPageRef.current = new Map([[1, null]]);
    }, [tenantId, pageSize]);

    const studentsQuery = useQuery({
        queryKey: ['students', tenantId, page, pageSize],
        queryFn: async () => {
            const cursor = cursorByPageRef.current.get(page);
            if (page > 1 && typeof cursor === 'undefined') {
                throw new Error('Pagination cursor missing. Please go page-by-page.');
            }
            const res = await fetchStudentsPage({ tenantId, pageSize, cursor: cursor ?? null });
            cursorByPageRef.current.set(page + 1, res.nextCursor || null);
            return res;
        },
        enabled: !!tenantId && includeRows,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const kpisQuery = useQuery({
        queryKey: ['studentKpis', tenantId],
        queryFn: () => fetchStudentKpis({ tenantId }),
        enabled: !!tenantId,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        rows: includeRows ? (studentsQuery.data?.rows || []) : [],
        total: includeRows ? (studentsQuery.data?.total || 0) : (kpisQuery.data?.totalStudents || 0),
        kpis: kpisQuery.data || { totalStudents: 0, activeStudents: 0, inactiveStudents: 0 },
        loading: includeRows ? (studentsQuery.isLoading || kpisQuery.isLoading) : kpisQuery.isLoading,
        error: studentsQuery.error || kpisQuery.error || null,
    };
}

